import { toTurkishLower } from '@/lib/customerSearch';

/**
 * Türkiye il/ilçe/mahalle veri kaynağı — src/data/turkeyLocations.ts'nin
 * (il/ilçe) tamamlayıcısıdır.
 *
 * Kaynak veri: İçişleri Bakanlığı Mülki İdare Bölümleri Envanteri temelli,
 * hazır JSON seti (81 il, 1000+ ilçe, 32.000+ mahalle) —
 * src/data/turkeyNeighborhoods.json. Format aynen korunur:
 *   { "İL_ADI": { "İLÇE_ADI": ["MAHALLE Mah.", ...] } }
 * Güncellemek için bu JSON dosyasını olduğu gibi değiştirmek yeterlidir,
 * kod tarafında başka hiçbir değişiklik gerekmez.
 *
 * PERFORMANS: JSON ~560KB (gzip ~140KB) olduğundan ana bundle'a hiç
 * girmez — yalnızca bir kullanıcı gerçekten il+ilçe seçtiğinde dinamik
 * import() ile bir kez indirilir ve modül belleğinde önbelleğe alınır
 * (aynı oturumda tekrar indirilmez).
 *
 * İSİM EŞLEŞTİRME: JSON kaynağı il/ilçe adlarını BÜYÜK HARF Türkçe olarak
 * tutar (ör. "GAZİANTEP", "NURDAĞI"); src/data/turkeyLocations.ts ise Baş
 * Harf Büyük biçimde (ör. "Gaziantep", "Nurdağı"). Mevcut müşteri/işletme/
 * teslimat kayıtları zaten turkeyLocations.ts'teki isimlerle kaydedildiği
 * için o dosyadaki il/ilçe adları DEĞİŞTİRİLMEDİ (bir isim değiştirilirse
 * eski kayıtların dropdown'da seçili görünmesi bozulurdu). Bunun yerine
 * eşleştirme Türkçe'ye duyarlı küçük harfe çevirerek (toTurkishLower)
 * yapılır — "Gaziantep" ile "GAZİANTEP" böylece güvenle eşleşir.
 *
 * Birkaç ilçe adı iki kaynak arasında gerçekten farklıdır (ör. resmi
 * envanterde Ankara/Kazan artık "Kahramankazan" olarak geçiyor;
 * turkeyLocations.ts'te hâlâ "Kazan"). Bu gibi nadir durumlarda eşleşme
 * bulunamaz ve mahalle listesi boş döner — form bunu "Mahalle bulunamadı"
 * mesajıyla gösterir, hataya düşmez (bkz. CityDistrictSelect.tsx).
 */

export type TurkeyNeighborhoodData = Record<string, Record<string, string[]>>;

let cachedData: TurkeyNeighborhoodData | null = null;
let loadingPromise: Promise<TurkeyNeighborhoodData> | null = null;

async function loadNeighborhoodData(): Promise<TurkeyNeighborhoodData> {
  if (cachedData) return cachedData;
  if (!loadingPromise) {
    loadingPromise = import('./turkeyNeighborhoods.json').then((mod) => {
      cachedData = (mod.default ?? mod) as unknown as TurkeyNeighborhoodData;
      return cachedData;
    });
  }
  return loadingPromise;
}

function findKey(obj: Record<string, unknown>, target: string): string | undefined {
  const targetLower = toTurkishLower(target);
  return Object.keys(obj).find((k) => toTurkishLower(k) === targetLower);
}

/**
 * Seçili il + ilçe için mahalle listesini döner (ör. "Mahalle Mah." biçiminde).
 * Veri henüz yoksa/eşleşme bulunamazsa boş dizi döner — hata fırlatmaz.
 */
export async function getNeighborhoods(city: string, district: string): Promise<string[]> {
  if (!city || !district) return [];
  try {
    const data = await loadNeighborhoodData();
    const cityKey = findKey(data, city);
    if (!cityKey) return [];
    const districtMap = data[cityKey];
    const districtKey = findKey(districtMap, district);
    if (!districtKey) return [];
    return districtMap[districtKey] || [];
  } catch {
    return [];
  }
}
